import React from 'react';
import { FormGroup, Label, Input, Row, Col, Button } from 'reactstrap';
import { Translate } from '../../../contexts/Language';
import { Modal } from '../../../components';
import countries from '../../../helpers/countries';

const beneficiaryTypes = [
  { id: 'INDIVIDUAL', name: Translate('Individual') },
  { id: 'CORPORATE', name: Translate('Corporate') },
  { id: 'UNKNOWN', name: Translate('Unknown') },
];

const beneficiaryRelationships = [
  {
    id: 'BRANCHOFFICE',
    name: Translate('Branch Office'),
    description: Translate('Represents an office or location that is part of a larger company but operates semi-independently.'),
  },
  {
    id: 'SPOUSE',
    name: Translate('Spouse'),
    description: Translate('Refers to the legally married partner of an individual.'),
  },
  {
    id: 'BUSINESSPARTNER',
    name: Translate('Business Partner'),
    description: Translate('Indicates a person or entity that shares ownership of a business or is engaged in a business venture with another.'),
  },
  {
    id: 'PARENT',
    name: Translate('Parent'),
    description: Translate('Refers to one\'s biological or adoptive parent.'),
  },
  {
    id: 'FRIEND',
    name: Translate('Friend'),
    description: Translate('Denotes a non - legal, personal relationship based on mutual affection outside professional or familial ties.'),
  },
  {
    id: 'HOLDINGCOMPANY',
    name: Translate('Holding Company'),
    description: Translate('A type of business entity that exists solely to own shares of other companies to form a corporate group.'),
  },
  {
    id: 'SUPPLIER',
    name: Translate('Supplier'),
    description: Translate('A person or company that provides goods or services to another entity under terms specified in a contract.'),
  },
  {
    id: 'EXSPOUSE',
    name: Translate('Ex-Spouse'),
    description: Translate('Refers to a person to whom one was formerly legally married but is now divorced.'),
  },
  {
    id: 'CHILDREN',
    name: Translate('Children'),
    description: Translate('Refers to one\'s biological or legally adopted children.'),
  },
  {
    id: 'FRANCHISEE',
    name: Translate('Franchisee'),
    description: Translate('Denotes an individual or company that has been granted the right by another business(the franchisor) to use the franchisor\'s branding and model to conduct business.'),
  },
  {
    id: 'NOTRELATED',
    name: Translate('Not Related'),
    description: Translate('Indicates no formal or recognized legal, familial, or business relationship exists.'),
  },
  {
    id: 'SELF',
    name: Translate('Self'),
    description: Translate('Represents oneself; used in contexts where an individual acts on their own behalf.'),
  },
  {
    id: 'CUSTOMER',
    name: Translate('Customer'),
    description: Translate('A person or entity that purchases goods or services from another business.'),
  },
  {
    id: 'EMPLOYEE',
    name: Translate('Employee'),
    description: Translate('An individual who is hired to provide services to a company on a regular basis in exchange for compensation and does not provide these services as part of an independent business.'),
  },
  {
    id: 'SUBSIDIARYCOMPANY',
    name: Translate('Subsidiary Company'),
    description: Translate('A company that is completely or partly owned and wholly controlled by another company(known as the parent company).'),
  },
  {
    id: 'CREDITOR',
    name: Translate('Creditor'),
    description: Translate('An entity or person to whom money is owed by another entity or person.'),
  },
  {
    id: 'DEBTOR',
    name: Translate('Debtor'),
    description: Translate('An entity or person who owes money to another.'),
  },
  {
    id: 'SIBLING',
    name: Translate('Sibling'),
    description: Translate('Refers to a brother or sister, either full or half.'),
  },
  {
    id: 'RELATIVE',
    name: Translate('Relative'),
    description: Translate('Refers to a family member who is not directly a parent, sibling, or child.'),
  },
];

export const BeneficiaryFormModal = ({
  show,
  isEditing,
  formData,
  onChange,
  onSave,
  onClose,
}) => (
  <Modal
    Show={show}
    Title={isEditing ? Translate('Edit Beneficiary') : Translate('Add Beneficiary')}
    onClose={onClose}
  >
    <form onSubmit={e => { e.preventDefault(); onSave(); }}>
      <FormGroup>
        <Label>{Translate('Name')}</Label>
        <Input
          value={formData.name}
          onChange={e => onChange('name', e.target.value)}
          required
        />
      </FormGroup>
      <FormGroup>
        <Label>{Translate('Email')}</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={e => onChange('email', e.target.value)}
          required
        />
      </FormGroup>
      <FormGroup>
        <Label>{Translate('Address')}</Label>
        <Input
          value={formData.address}
          onChange={e => onChange('address', e.target.value)}
          required
        />
      </FormGroup>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>{Translate('City')}</Label>
            <Input
              value={formData.city}
              onChange={e => onChange('city', e.target.value)}
              required
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>{Translate('State')}</Label>
            <Input
              value={formData.state}
              onChange={e => onChange('state', e.target.value)}
              required
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>{Translate('Country')}</Label>
            <Input
              type="select"
              value={formData.country}
              onChange={e => onChange('country', e.target.value)}
              required
            >
              {
                  countries.map(c => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))
                }
            </Input>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>{Translate('Zip Code')}</Label>
            <Input
              value={formData.zipcode}
              onChange={e => onChange('zipcode', e.target.value)}
              required
            />
          </FormGroup>
        </Col>
      </Row>
      <FormGroup>
        <Label>{Translate('Phone Number')}</Label>
        <Input
          value={formData.phone}
          onChange={e => onChange('phone', e.target.value)}
          required
        />
      </FormGroup>
      <FormGroup>
        <Label>{Translate('Type')}</Label>
        <Input
          type="select"
          value={formData.type}
          onChange={e => onChange('type', e.target.value)}
          required
        >
          <option value="">{Translate('Select Type')}</option>
          {beneficiaryTypes.map(pm => (
            <option key={pm.id} value={pm.id}>
              {pm.name}
            </option>
          ))}
        </Input>
      </FormGroup>
      <FormGroup>
        <Label>{Translate('Relationship')}</Label>
        <Input
          type="select"
          value={formData.relationship}
          onChange={e => onChange('relationship', e.target.value)}
          required
        >
          <option value="">{Translate('Select Relationship')}</option>
          {beneficiaryRelationships.map(pm => (
            <option
              key={pm.id}
              value={pm.id}
              title={pm.description}
            >
              {pm.name}
              {/* <span>{pm.name}</span> */}
              {/* <small style={{ fontSize: '0.8em', color: '#666' }}>
                  {pm.description}
                </small> */}
            </option>
          ))}
        </Input>
      </FormGroup>
      <div className="text-right">
        <Button color="secondary" onClick={onClose} className="mr-2">
          {Translate('Cancel')}
        </Button>
        <Button color="primary" type="submit">
          {Translate('Save')}
        </Button>
      </div>
    </form>
  </Modal>
);
